import React, { useRef, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import LoanApplicationPDF from "./LoanApplicationPDF";
import { useSelector } from "react-redux";
import { selectFiles, selectFormData } from '../../Store/userSlice';
import { downloadAllDocuments } from "../../services/loanService";
import { toast } from "react-toastify";

const getLoanApplicationUser = () => {
  try {
    const raw = localStorage.getItem("loanApplicationUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getLocalStorageUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const PreviewApplication = ({ open, onClose, handleSubmit, data: externalData, readOnly = false }) => {
  const pdfRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const data = useSelector(selectFormData);
  const loanDoc = useSelector(selectFiles);
  const reduxUser = useSelector((state) => state?.user?.user);
  const effectiveData = externalData ?? data;
  const userObj = Array.isArray(reduxUser) ? reduxUser[0] : reduxUser;
  const u = getLoanApplicationUser() ?? userObj ?? getLocalStorageUser();
  const userInfo = {
    Name: u?.name ?? u?.Name ?? u?.username ?? effectiveData?.name,
    Email: u?.email ?? u?.Email ?? effectiveData?.email,
    MobileNumber: u?.mobileNumber ?? u?.MobileNumber ?? u?.phoneNumber ?? effectiveData?.mobileNo,
  };
  const combinedData = { ...effectiveData, documents: effectiveData?.mcaDocuments ?? effectiveData?.documents ?? [], mcaDocxStatus: effectiveData?.mcaDocxStatus, loanDoc: externalData?.loanDoc ?? loanDoc ?? {}, userInfo };

  const isSuperAdmin = useMemo(() => userObj?.role === 'SUPERADMIN' || getLocalStorageUser()?.role === 'SUPERADMIN', [userObj?.role]);

  const handleDownload = () => {
    if (pdfRef.current) {
      pdfRef.current.downloadPDF();
    }
  };

  const handleDownloadAllDocuments = async () => {
    const { applicationId: appId, applicationNumber: appNumber } = combinedData || {};
    if (!appId) return;
    let downloadUrl;
    try {
      const res = await downloadAllDocuments(appId);
      const zipFile = new Blob([res?.data], { type: 'application/zip' });
      downloadUrl = URL.createObjectURL(zipFile);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = `${appNumber || appId}_documents.zip`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      toast.error("Failed to download all documents", error);
    } finally {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    }
  };

  const handleSubmitClick = async (e) => {
    setIsSubmitting(true);
    try {
      await handleSubmit(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      {/* Header with title + download */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 2,
        }}
      >
        <Typography variant="h6">Application Preview</Typography>
        <Box>
          {isSuperAdmin && Array.isArray(combinedData?.documents) && combinedData.documents.length > 0 && (
            <Button onClick={handleDownloadAllDocuments} variant="outlined" color="primary" sx={{ mr: 1 }} startIcon={<DownloadIcon />}>
              Download All Documents
            </Button>
          )}
          <IconButton onClick={handleDownload} color="primary">
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* PDF Preview (render LoanApplicationPDF content) */}
      <DialogContent dividers sx={{ height: "80vh", overflowY: "auto" }}>
        <LoanApplicationPDF ref={pdfRef} data={combinedData} />
      </DialogContent>

      {/* Actions */}
      {!readOnly && (
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitClick}
            variant="contained"
            color="primary"
            disableElevation
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PreviewApplication;
